import { useRef, useState, useCallback, useEffect } from 'react';

// useDragResize: Handles vertical and horizontal drag/resize logic
export function useDragResize({
  onVerticalResize,
  onHorizontalResize,
  minVertical = 0.2,
  maxVertical = 0.8,
  minHorizontal = 50,
  maxHorizontal = 0.6,
  getVerticalRef,
  getHorizontalRef,
}) {
  const [isDraggingVertical, setIsDraggingVertical] = useState(false);
  const [isDraggingHorizontal, setIsDraggingHorizontal] = useState(false);
  const startDragX = useRef(0);
  const startDragY = useRef(0);
  const startWidth = useRef(0);
  const startHeight = useRef(0);
  const animationFrameRef = useRef(null);

  const handleVerticalMouseDown = useCallback((e) => {
    e.preventDefault();
    startDragX.current = e.clientX;
    if (getVerticalRef && getVerticalRef().current) {
      startWidth.current = getVerticalRef().current.offsetWidth;
    }
    setIsDraggingVertical(true);
  }, [getVerticalRef]);

  const handleHorizontalMouseDown = useCallback((e) => {
    e.preventDefault();
    startDragY.current = e.clientY;
    if (getHorizontalRef && getHorizontalRef().current) {
      startHeight.current = getHorizontalRef().current.clientHeight;
    }
    setIsDraggingHorizontal(true);
  }, [getHorizontalRef]);

  useEffect(() => {
    function handleMouseMove(e) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      animationFrameRef.current = requestAnimationFrame(() => {
        if (isDraggingVertical && getVerticalRef && getVerticalRef().current) {
          const delta = e.clientX - startDragX.current;
          const containerWidth = window.innerWidth;
          const newWidth = startWidth.current + delta;
          const minWidth = containerWidth * minVertical;
          const maxWidth = containerWidth * maxVertical;
          const constrainedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
          if (onVerticalResize) {
            onVerticalResize(`${(constrainedWidth / containerWidth) * 100}%`);
          }
        }
        if (isDraggingHorizontal && getHorizontalRef && getHorizontalRef().current) {
          const delta = startDragY.current - e.clientY;
          const newHeight = startHeight.current + delta;
          const minHeight = minHorizontal;
          const maxHeight = window.innerHeight * maxHorizontal;
          const constrainedHeight = Math.min(Math.max(newHeight, minHeight), maxHeight);
          if (onHorizontalResize) {
            onHorizontalResize(`${constrainedHeight}px`);
          }
        }
      });
    }
    
    function handleMouseUp() {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setIsDraggingVertical(false);
      setIsDraggingHorizontal(false);
    }
    
    if (isDraggingVertical || isDraggingHorizontal) {
      window.addEventListener('mousemove', handleMouseMove, { passive: true });
      window.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingVertical, isDraggingHorizontal, onVerticalResize, onHorizontalResize, minVertical, maxVertical, minHorizontal, maxHorizontal, getVerticalRef, getHorizontalRef]);

  return {
    isDraggingVertical,
    isDraggingHorizontal,
    handleVerticalMouseDown,
    handleHorizontalMouseDown,
  };
}

// useDebouncedUrlSync: Handles debounced syncing of a value to the URL
export function useDebouncedUrlSync({ value, param, setUrlParameter, removeUrlParameter, delay = 500 }) {
  useEffect(() => {
    const handler = setTimeout(() => {
      if (value) {
        setUrlParameter(param, value);
      } else {
        removeUrlParameter(param);
      }
    }, delay);
    return () => clearTimeout(handler);
  }, [value, param, setUrlParameter, removeUrlParameter, delay]);
}
